import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { InfoIcon, Poppins } from 'src/common';
import { ConsequencesSelect, EventsSelect, SourcesSelect } from 'src/components/select';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { generateCsvTemplateLink } from './util';
import { MissingVariablesForm } from './MissingVariablesForm';
import { useScenarioCsvUpload } from './useScenarioCsvUpload';
import { pluralize } from 'src/utils/misc';
import { GenericSelect } from 'src/components/select/GenericSelect';
import _ from 'lodash';
import {
  CsvDropzoneWithTemplate,
  CsvValidationErrors,
  CsvUploadButtons,
  SCVSummary,
  DzoneWrap,
} from 'src/components/CsvUpload';

const pluralizeScenarios = (count: number) => pluralize({ count, singular: 'scenario', plural: 'scenarios' });
const pluralizeVariables = (count: number) => pluralize({ count, singular: 'variable', plural: 'variables' });

interface FromCsvProps {
  assessmentId: string;
  onClose: () => void;
  setIsScvDirty: (isDirty: boolean) => void;
  existingScenarioIds: string[];
}

export const FromCsv: React.FC<FromCsvProps> = ({ assessmentId, onClose, setIsScvDirty, existingScenarioIds }) => {
  const [hasMissingVariableErrors, setHasMissingVariableErrors] = useState(false);
  const { state, handlers } = useScenarioCsvUpload({ assessmentId, setIsScvDirty });
  const {
    file,
    error,
    isUploadSuccessful,
    validationErrors,
    validScenarios,
    isUploading,
    csvVariables,
    manualVariables,
    missingVariableNames,
    workspaceVariables,
    assessmentVariables,
  } = state;

  const { onDrop, saveScenarios, resetUpload, setManualVariables } = handlers;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
  });

  const newCount = validScenarios.filter((s) => !existingScenarioIds.includes(s.id ?? '')).length;
  const updatedCount = validScenarios.filter((s) => existingScenarioIds.includes(s.id ?? '')).length;

  return (
    <DzoneWrap className="h-padding">
      <CsvDropzoneWithTemplate
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isDragActive={isDragActive}
        file={file}
        templateLink={generateCsvTemplateLink()}
        templateFileName="scenario_template.csv"
        onTemplateClick={() =>
          mpEvent(MPEvents.DownloadCSVTemplate, {
            modal: 'Scenario add modal',
            tags: ['SCENARIO'],
          })
        }
      />

      {missingVariableNames.length > 0 && (
        <MissingVariablesForm
          missingVariableNames={missingVariableNames}
          csvVariables={csvVariables}
          manualVariables={manualVariables}
          onManualVariablesChange={setManualVariables}
          onValidityChange={setHasMissingVariableErrors}
        />
      )}

      <CsvValidationErrors errors={validationErrors} />

      {file && (
        <>
          {!isUploadSuccessful && (
            <SCVSummary
              items={[
                'File validated',
                `${updatedCount} ${pluralizeScenarios(updatedCount)} ready to be updated`,
                `${newCount} ${pluralizeScenarios(newCount)} ready to be created`,
                `${missingVariableNames.length} ${pluralizeVariables(missingVariableNames.length)} ready to be created`,
              ]}
            />
          )}
          {isUploadSuccessful && (
            <SCVSummary
              isCompleted
              items={[
                `${updatedCount} existing ${pluralizeScenarios(updatedCount)} updated`,
                `${newCount} new ${pluralizeScenarios(newCount)} added`,
                `${missingVariableNames.length} ${pluralizeVariables(missingVariableNames.length)} added`,
                'File upload successful',
              ]}
            />
          )}
        </>
      )}

      <div>
        <InfoIcon style={{ marginRight: 6, transform: 'scale(0.8)' }} />
        <Poppins px={14}>
          Use # to reference variables listed below in scenario formulas. If an assessment-specific value exists, it
          takes priority over the workspace default.
        </Poppins>
      </div>

      <CsvUploadButtons
        isUploadSuccessful={isUploadSuccessful}
        hasFile={!!file}
        isDisabled={!!validationErrors.length || isUploading || hasMissingVariableErrors}
        onCancel={onClose}
        onUpload={saveScenarios}
        onSelectFile={open}
        onUploadAnother={resetUpload}
        onDone={onClose}
      />

      {!file && (
        <>
          <Poppins px={14} weight={500}>
            Workspace and assessment variables drop down lists below. The valid values of Sources, Events and
            Consequences are also listed in below. Blank is also valid for each.
          </Poppins>
          <div className="grid-list grid-list--5">
            <GenericSelect
              options={workspaceVariables.map((el) => ({ label: `#${_.snakeCase(el.name)}`, value: el.id }))}
              menuPlacement="top"
              placeholder="WORKSPACE VARIABLES"
              onChange={() => {}}
            />
            <GenericSelect
              options={(assessmentVariables ?? []).map((el) => ({ label: `#${_.snakeCase(el.name)}`, value: el.id }))}
              menuPlacement="top"
              placeholder="ASSESSMENT VARIABLES"
              onChange={() => {}}
            />
            <SourcesSelect placeholder="SOURCES" menuPlacement="top" />
            <EventsSelect placeholder="EVENTS" menuPlacement="top" />
            <ConsequencesSelect placeholder="CONSEQUENCES" menuPlacement="top" />
          </div>
        </>
      )}

      {error && <div className="error">{error}</div>}
    </DzoneWrap>
  );
};
